import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export function SaveAsDialog({ open, onOpenChange, initialTitle, onConfirm, saving }) {
  const [title, setTitle] = useState(initialTitle || "");
  useEffect(() => {
    if (open) setTitle(initialTitle ? `${initialTitle} (salinan)` : "");
  }, [open, initialTitle]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="save-as-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Simpan Sebagai</DialogTitle>
          <DialogDescription>Buat perhitungan baru dengan judul berbeda.</DialogDescription>
        </DialogHeader>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Judul perhitungan"
          className="h-10 bg-[hsl(220_12%_8%)]"
          data-testid="save-as-title-input"
          autoFocus
        />
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="save-as-cancel-button">
            Batal
          </Button>
          <Button
            onClick={async () => {
              if (!title.trim()) return toast.error("Judul wajib diisi");
              const ok = await onConfirm(title.trim());
              if (ok) onOpenChange(false);
            }}
            disabled={saving}
            data-testid="save-as-confirm-button"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ShareDialog({ open, onOpenChange, calcId, shares = [], onSaved }) {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState("view");
  const [dir, setDir] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setList(shares.map((s) => ({ email: s.email, permission: s.permission })));
    setEmail("");
    setPerm("view");
    api
      .get("/users/directory")
      .then(({ data }) => setDir(data.users || []))
      .catch(() => setDir([]));
  }, [open, shares]);

  const suggestions = useMemo(() => {
    const q = email.trim().toLowerCase();
    const taken = new Set(list.map((l) => l.email));
    return dir.filter((u) => !taken.has(u.email) && (!q || u.email.includes(q))).slice(0, 6);
  }, [dir, email, list]);

  const add = (value) => {
    const e = (value ?? email).trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Masukkan email yang valid");
    if (list.some((l) => l.email === e)) return toast.error("Email sudah ada di daftar");
    setList((l) => [...l, { email: e, permission: perm }]);
    setEmail("");
  };

  const saveShares = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/calculations/${calcId}/shares`, { shares: list });
      toast.success("Akses berbagi diperbarui");
      onSaved && onSaved(data.shares || []);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan akses"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Bagikan Perhitungan
          </DialogTitle>
          <DialogDescription>
            Bagikan ke rekan dengan email yang diizinkan. Tentukan akses per orang: <b>Lihat saja</b> atau <b>Bisa edit</b>.
          </DialogDescription>
        </DialogHeader>
        {!calcId ? (
          <div className="text-sm text-muted-foreground py-4" data-testid="share-dialog-unsaved">
            Simpan perhitungan terlebih dahulu sebelum dibagikan.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="nama@thrive.co.id"
                className="h-10 bg-[hsl(220_12%_8%)] flex-1"
                data-testid="share-email-input"
              />
              <Select value={perm} onValueChange={setPerm}>
                <SelectTrigger className="w-[130px] h-10 bg-[hsl(220_12%_8%)]" data-testid="share-permission-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">Lihat saja</SelectItem>
                  <SelectItem value="edit">Bisa edit</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => add()} size="icon" className="h-10 w-10" data-testid="share-add-button">
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="share-suggestions">
                {suggestions.map((u) => (
                  <button
                    key={u.email}
                    onClick={() => add(u.email)}
                    className="press text-xs rounded-full border border-border px-2.5 py-1 text-muted-foreground hover:text-foreground hover:border-[color:var(--hairline)]"
                    data-testid={`share-suggestion-${u.email}`}
                  >
                    + {u.email}
                  </button>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-border divide-y divide-border" data-testid="share-list">
              {list.length === 0 && <div className="p-4 text-sm text-muted-foreground">Belum dibagikan ke siapa pun.</div>}
              {list.map((s, i) => (
                <div key={s.email} className="flex items-center gap-2 p-2.5" data-testid={`share-row-${i}`}>
                  <span className="flex-1 text-sm truncate">{s.email}</span>
                  <Select
                    value={s.permission}
                    onValueChange={(v) => setList((l) => l.map((x) => (x.email === s.email ? { ...x, permission: v } : x)))}
                  >
                    <SelectTrigger className="w-[120px] h-8 text-xs bg-[hsl(220_12%_8%)]" data-testid={`share-row-permission-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">Lihat saja</SelectItem>
                      <SelectItem value="edit">Bisa edit</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_62%)]"
                    onClick={() => setList((l) => l.filter((x) => x.email !== s.email))}
                    data-testid={`share-row-remove-${i}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="share-cancel-button">
            Tutup
          </Button>
          {calcId && (
            <Button onClick={saveShares} disabled={saving} data-testid="share-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Akses
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
